import type { Workbook, Worksheet } from 'exceljs';
import { DayIntervals, Statistics, ActivityType, StatisticItem } from '../../../../../model/activity.model';
import { formattedDuration } from '../../../../../utils/dateTime';
import { getDuration } from '../DaysIntervals/helper';
import { statisticsItemTitle } from '../ActivityPie/helper';
import dayjs from 'dayjs';

const HEADER_FILL = { type: 'pattern' as const, pattern: 'solid' as const, fgColor: { argb: 'FF523D5C' } };
const HEADER_FONT = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
const BOLD_FONT = { bold: true, size: 11 };
const DATE_NUM_FMT = 'yyyy-mm-dd hh:mm';

function setColumnWidths(ws: Worksheet, widths: number[]) {
  widths.forEach((w, i) => {
    ws.getColumn(i + 1).width = w;
  });
}

/**
 * Tab 1: "History" - intervals grouped by day
 */
export function addHistorySheet(
  wb: Workbook,
  daysIntervals: DayIntervals[],
  activityTypes: ActivityType[]
) {
  const ws = wb.addWorksheet('Day History');
  const columns = ['Activity', 'Start', 'Finish', 'Duration', 'Tags', 'Comment'];
  setColumnWidths(ws, [30, 18, 18, 12, 20, 30]);

  // Header row
  const headerRow = ws.addRow(columns);
  headerRow.eachCell(cell => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = { vertical: 'middle' };
  });

  daysIntervals.forEach(day => {
    // Calculate day total duration
    const dayTotalMs = day.intervals.reduce((sum, iv) => {
      return sum + dayjs(iv.finish).diff(dayjs(iv.start));
    }, 0);
    const dayTotalStr = formattedDuration(dayTotalMs);

    // Derive date from first interval instead of using title (which may say "Today"/"Yesterday")
    const dayDate = day.intervals.length > 0
      ? dayjs(day.intervals[0].start).format('YYYY-MM-DD')
      : day.title;

    // Day header row - merged across all columns
    const dayRow = ws.addRow([`${dayDate}  (${dayTotalStr})`, '', '', '', '', '']);
    ws.mergeCells(dayRow.number, 1, dayRow.number, 6);
    dayRow.getCell(1).font = { bold: true, size: 12 };
    dayRow.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF0ECF3' } };
    dayRow.getCell(1).alignment = { vertical: 'middle' };

    // Interval rows
    day.intervals.forEach(interval => {
      const actType = activityTypes.find(t => t.id === interval.typeId);
      const row = ws.addRow([
        actType?.name || 'Unknown',
        new Date(interval.start),
        new Date(interval.finish),
        getDuration(interval),
        (interval.tags || []).join(', '),
        interval.comment || ''
      ]);
      row.getCell(2).numFmt = 'hh:mm';
      row.getCell(3).numFmt = 'hh:mm';
    });
  });
}

/**
 * Tab 2: "Statistics" - with collapsible outline grouping
 */
export function addStatisticsSheet(
  wb: Workbook,
  statistics: Statistics
) {
  const ws = wb.addWorksheet('Statistics');
  setColumnWidths(ws, [35, 15]);

  // Parent rows are above children, not below
  ws.properties.outlineProperties = { summaryBelow: false, summaryRight: false };

  // Header row
  const headerRow = ws.addRow(['Activity', 'Duration']);
  headerRow.eachCell(cell => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = { vertical: 'middle' };
  });

  // Total row
  const totalDuration = formattedDuration(statistics.total.info.total * 1000);
  const totalRow = ws.addRow(['Total', totalDuration]);
  totalRow.getCell(1).font = BOLD_FONT;
  totalRow.getCell(2).font = BOLD_FONT;

  // Always use groupedStatistics for the expandable hierarchy
  const statsItems = statistics.total.groupedStatistics;
  const types = statistics.types;

  function addStatItems(items: StatisticItem[], outlineLevel: number) {
    items.forEach(item => {
      const name = statisticsItemTitle(types, item);
      const dur = formattedDuration(item.duration * 1000);

      const row = ws.addRow([name, dur]);
      row.getCell(1).font = { bold: outlineLevel === 0 };

      if (outlineLevel > 0) {
        row.outlineLevel = outlineLevel;
      }

      // Recurse into children
      if (item.children && item.children.length > 0) {
        addStatItems(item.children, outlineLevel + 1);
      }
    });
  }

  addStatItems(statsItems, 0);
}

/**
 * Tab 3: "Records" - flat list of all intervals
 */
export function addRecordsSheet(
  wb: Workbook,
  daysIntervals: DayIntervals[],
  activityTypes: ActivityType[]
) {
  const ws = wb.addWorksheet('Records');
  const columns = ['Activity', 'Start', 'Finish', 'Duration', 'Tags', 'Comment'];
  setColumnWidths(ws, [30, 18, 18, 12, 20, 30]);

  // Header row
  const headerRow = ws.addRow(columns);
  headerRow.eachCell(cell => {
    cell.fill = HEADER_FILL;
    cell.font = HEADER_FONT;
    cell.alignment = { vertical: 'middle' };
  });

  daysIntervals.forEach(day => {
    day.intervals.forEach(interval => {
      const actType = activityTypes.find(t => t.id === interval.typeId);
      const row = ws.addRow([
        actType?.name || 'Unknown',
        new Date(interval.start),
        new Date(interval.finish),
        getDuration(interval),
        (interval.tags || []).join(', '),
        interval.comment || ''
      ]);
      row.getCell(2).numFmt = DATE_NUM_FMT;
      row.getCell(3).numFmt = DATE_NUM_FMT;
    });
  });
}
